package fr.frux.feature.map.site

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapBounds
import fr.frux.shared.domain.model.Site
import fr.frux.shared.domain.model.isLocked
import fr.frux.shared.domain.repository.SiteRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * Plafond du lot de lieux demande avec les arbres. Plus bas que celui des arbres : un lieu en
 * regroupe plusieurs, et une plaque prend plus de place qu'une epingle.
 */
const val SITE_MAP_LIMIT = 300

/** Aire interrogee par la carte, telle que [fr.frux.feature.map.MapViewModel] vient de la demander pour ses arbres. */
data class MapArea(
    val center: Location,
    val radiusKm: Double,
    val bounds: MapBounds?,
)

/** Avis ponctuel de la couche lieux, consomme par l'ecran qui l'affiche. */
sealed interface SiteNotice {
    data class Created(
        val siteId: Long,
    ) : SiteNotice

    data object Removed : SiteNotice
}

data class SiteLayerState(
    val sites: List<Site> = emptyList(),
    val selectedSiteId: Long? = null,
    val notice: SiteNotice? = null,
) {
    val selectedSite: Site? get() = sites.firstOrNull { it.id == selectedSiteId }

    /** Calcule une fois par etat : la surface carte le lit a chaque recomposition. */
    val siteIds: Set<Long> = sites.mapTo(HashSet()) { it.id }

    /** Lieux prives sans recolte a venir : leurs arbres portent le cadenas au zoom de detail. */
    fun lockedSiteIds(nowMillis: Long): Set<Long> = sites.filter { it.isLocked(nowMillis) }.mapTo(HashSet()) { it.id }
}

/**
 * Couche des lieux de cueillette sur la carte (ADR 0035), a part du [fr.frux.feature.map.MapViewModel]
 * qui est au plafond de taille. Elle ne suit pas la camera : elle charge l'aire que le ViewModel des
 * arbres vient d'interroger, donc une requete de lieux par chargement d'arbres, jamais par geste.
 */
class SiteLayerViewModel(
    private val siteRepository: SiteRepository,
) : ViewModel() {
    private val _state = MutableStateFlow(SiteLayerState())
    val state: StateFlow<SiteLayerState> = _state.asStateFlow()

    private var loadJob: Job? = null

    /** Derniere aire demandee, rechargee a chaque ecriture de lieu de la session. */
    private var requestedArea: MapArea? = null

    /** Derniere aire chargee avec succes : la redemander ne relance rien. */
    private var loadedArea: MapArea? = null

    /**
     * Lieu annonce par [announceCreated], garde dans chaque lot tant qu'aucun ne le contient. La
     * premiere emission d'un chargement vient du cache local, qui peut l'ignorer : sans lui, la
     * plaque et la carte de resume disparaitraient avant la reponse reseau.
     */
    private var pendingSite: Site? = null

    init {
        viewModelScope.launch {
            // Meme aire que la derniere fois : le lieu ecrit a pu changer d'effectif, de regle ou disparaitre.
            siteRepository.changes.collect { requestedArea?.let(::load) }
        }
        // Un 404 decouvert par une fiche : sans cela la plaque survivrait jusqu'au redemarrage.
        viewModelScope.launch { siteRepository.removals.collect(::drop) }
    }

    fun onAreaRequested(area: MapArea) {
        val alreadyCovered = area == loadedArea || loadJob?.isActive == true
        if (area == requestedArea && alreadyCovered) return
        load(area)
    }

    fun select(siteId: Long) {
        _state.update { it.copy(selectedSiteId = if (it.selectedSiteId == siteId) null else siteId) }
    }

    fun clearSelection() {
        _state.update { it.copy(selectedSiteId = null) }
    }

    /** Lieu tout juste cree : visible et selectionne sans attendre le prochain lot. */
    fun announceCreated(site: Site) {
        pendingSite = site
        _state.update { state ->
            val sites = if (state.sites.any { it.id == site.id }) state.sites else state.sites + site
            state.copy(sites = sites, selectedSiteId = site.id, notice = SiteNotice.Created(site.id))
        }
    }

    fun announceRemoved(siteId: Long) {
        drop(siteId)
        _state.update { it.copy(notice = SiteNotice.Removed) }
    }

    /** Retire le lieu sans avis : l'ecran qui l'a decouvert introuvable a deja prevenu l'utilisateur. */
    private fun drop(siteId: Long) {
        if (pendingSite?.id == siteId) pendingSite = null
        _state.update { state ->
            state.copy(
                sites = state.sites.filterNot { it.id == siteId },
                selectedSiteId = state.selectedSiteId.takeUnless { it == siteId },
            )
        }
    }

    fun consumeNotice() {
        _state.update { it.copy(notice = null) }
    }

    /** Purge au changement d'utilisateur, comme le reste de la surface persistante (ADR 0009). */
    fun reset() {
        loadJob?.cancel()
        loadJob = null
        requestedArea = null
        loadedArea = null
        pendingSite = null
        _state.value = SiteLayerState()
    }

    private fun load(area: MapArea) {
        loadJob?.cancel()
        requestedArea = area
        loadJob =
            viewModelScope.launch {
                var latest: List<Site>? = null
                siteRepository
                    .getNearbySites(
                        lat = area.center.latitude,
                        lon = area.center.longitude,
                        radiusKm = area.radiusKm,
                        bounds = area.bounds,
                        limit = SITE_MAP_LIMIT,
                    ).collect { result ->
                        result.fold(
                            onSuccess = { nearby ->
                                loadedArea = area
                                latest = nearby.sites
                                _state.update { it.withSites(nearby.sites) }
                            },
                            // Sous le niveau erreur : les plaques deja affichees restent justes, rien a remonter.
                            // Ni position ni message : celui d'une erreur reseau porte l'URL, donc lat/lon.
                            onFailure = {
                                latest = null
                                Logger.i(TAG, "Lieux non charges (${it::class.simpleName})")
                            },
                        )
                    }
                // Derniere emission = reponse reseau : un lieu annonce dans l'aire qu'elle ne rend pas
                // n'existe plus (supprime ou masque ailleurs).
                // Un lot plein a pu etre coupe par le serveur : il ne prouve pas l'absence du lieu.
                val fresh = latest?.takeIf { it.size < SITE_MAP_LIMIT }
                val pending = pendingSite
                if (fresh != null && pending != null && area.covers(pending)) {
                    pendingSite = null
                    _state.update { it.withSites(fresh) }
                }
            }
    }

    private fun SiteLayerState.withSites(fetched: List<Site>): SiteLayerState {
        val pending = pendingSite
        if (pending != null && fetched.any { it.id == pending.id }) pendingSite = null
        val sites = pendingSite?.let { fetched + it } ?: fetched
        return copy(sites = sites, selectedSiteId = selectedSiteId.takeIf { id -> sites.any { it.id == id } })
    }

    /**
     * Vrai si la reponse du serveur aurait du contenir [site] : dans le rayon et, quand la requete est
     * bornee, dans la bbox. Hors de l'aire, son absence ne prouve rien.
     */
    private fun MapArea.covers(site: Site): Boolean {
        val inRadius = center.distanceMetersTo(Location(site.latitude, site.longitude)) <= radiusKm * METERS_PER_KM
        val box = bounds
        val inBounds =
            box == null ||
                (
                    site.latitude in box.southwest.latitude..box.northeast.latitude &&
                        site.longitude in box.southwest.longitude..box.northeast.longitude
                )
        return inRadius && inBounds
    }

    private companion object {
        const val TAG = "SiteLayerViewModel"
        const val METERS_PER_KM = 1000.0
    }
}
